/**
 * Camera barcode scanning utility using ZXing browser library
 * Supports both 1D and 2D barcodes including PDF417 (IATA standard)
 */

import { BrowserMultiFormatReader } from '@zxing/browser'

/**
 * Camera scanner class for detecting IATA barcodes (including PDF417)
 */
export class CameraScanner {
  constructor() {
    this.isScanning = false
    this.codeReader = null
    this.videoElement = null
    this.scannerControls = null
    this.scanId = 0 // Track scan sessions to ignore stale callbacks
  }

  /**
   * Initialize the barcode reader with PDF417 support
   */
  async initialize() {
    // Use @zxing/browser which has better mobile camera support
    // BrowserMultiFormatReader supports all formats including PDF417 by default
    this.codeReader = new BrowserMultiFormatReader()
    console.log('Camera scanner ready (ZXing Browser) - PDF417 support enabled')
  }

  /**
   * List available video input devices (cameras)
   * @returns {Promise<MediaDeviceInfo[]>}
   */
  async listCameras() {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'videoinput')
  }

  /**
   * Start scanning for barcodes using ZXing
   * @param {HTMLVideoElement} videoElement - Video element to render camera stream
   * @param {string} deviceId - Camera device ID
   * @param {Function} onSuccess - Callback when barcode detected (barcode: string) => void
   * @param {Function} onError - Callback on error (error: Error) => void
   */
  async startScanning(videoElement, deviceId, onSuccess, onError) {
    try {
      // Increment scan ID to invalidate old callbacks
      this.scanId++
      const currentScanId = this.scanId

      this.isScanning = true
      this.videoElement = videoElement

      console.log('Starting ZXing continuous scan for PDF417...', {
        scanId: currentScanId,
        deviceId: deviceId || 'default',
        videoElement: videoElement ? 'provided' : 'missing',
      })

      // Build constraints with HD resolution and autofocus for better barcode detection
      const videoConstraints = {
        width: { ideal: 2560 },
        height: { ideal: 1440 },
        focusMode: { ideal: 'continuous' },
      }
      if (deviceId) {
        videoConstraints.deviceId = { exact: deviceId }
      } else {
        videoConstraints.facingMode = 'environment'
      }

      const constraints = {
        video: videoConstraints,
        audio: false,
      }

      let scanCount = 0
      let lastLogTime = Date.now()

      // Use decodeFromConstraints for HD resolution scanning
      this.scannerControls = await this.codeReader.decodeFromConstraints(
        constraints,
        videoElement,
        (result, err) => {
          // Ignore callbacks from old scan sessions
          if (currentScanId !== this.scanId || !this.isScanning) {
            return // Silently ignore - don't log
          }

          scanCount++

          // Log scanning activity every 2 seconds
          const now = Date.now()
          if (now - lastLogTime > 2000) {
            console.log('ZXing scanning...', {
              attempts: scanCount,
              hasResult: !!result,
              hasError: !!err,
              errorType: err ? err.name : 'none',
            })
            lastLogTime = now
          }

          if (result) {
            const code = result.getText()
            const format = result.getBarcodeFormat()

            console.log('✅ Barcode detected!', {
              codePreview: code.substring(0, 50) + '...',
              fullCodeLength: code.length,
              format: format,
            })

            // IATA barcodes are typically 50-200+ characters
            // PDF417 boarding passes are usually 100+ characters
            if (code && code.length >= 20) {
              console.log('✅ Valid IATA barcode, passing full code to parser')
              console.log('Full barcode:', code)
              this.stopScanning()
              onSuccess(code)
            } else {
              console.warn(
                '⚠️ Barcode too short for IATA (need 20+ chars), ignoring:',
                code,
                'length:',
                code.length,
              )
            }
          }

          // Log non-NotFoundException errors
          if (err && err.name !== 'NotFoundException') {
            console.error('ZXing decode error:', {
              name: err.name,
              message: err.message,
              stack: err.stack,
            })
          }
        },
      )

      console.log('ZXing scanner started successfully')
    } catch (error) {
      this.isScanning = false
      console.error('Camera initialization error:', error)
      onError(error)
    }
  }

  /**
   * Stop scanning and clean up video stream
   */
  stopScanning() {
    console.log('Stopping ZXing scanner...', { isScanning: this.isScanning })

    // Set flag first to prevent callbacks from processing
    this.isScanning = false

    try {
      // Use scannerControls.stop() which cleanly stops decoding and video tracks
      if (this.scannerControls) {
        this.scannerControls.stop()
        this.scannerControls = null
      }

      // Ensure video tracks are stopped as fallback
      if (this.videoElement && this.videoElement.srcObject) {
        var stream = this.videoElement.srcObject
        stream.getTracks().forEach(function (track) {
          track.stop()
        })
        this.videoElement.srcObject = null
      }

      console.log('✅ Scanner stopped successfully')
    } catch (err) {
      console.warn('Error stopping scanner:', err)
    }

    this.videoElement = null
  }

  /**
   * Clean up and dispose of resources
   */
  dispose() {
    this.stopScanning()
  }
}
